import axios from "axios";
import {
  recordReadCache,
  serveFromCache,
  enqueueOperation,
  invalidatePath,
  shouldSkip,
  initOfflineSync,
  syncQueue,
} from "@/lib/offlineSync";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("amal_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  // --- Success: cache GETs, invalidate caches on writes ---
  async (res) => {
    try {
      const url = res.config.url || "";
      const method = (res.config.method || "get").toLowerCase();
      if (!shouldSkip(url)) {
        if (method === "get" && res.status >= 200 && res.status < 300) {
          await recordReadCache(method, url, res.data);
        } else if (["post", "put", "patch", "delete"].includes(method)) {
          // Invalidate the related list cache (best-effort)
          const root = url.split("?")[0].split("/").slice(0, 2).join("/");
          if (root) await invalidatePath(root);
        }
      }
    } catch {
      /* ignore cache errors */
    }
    return res;
  },
  // --- Error: 401 = logout; otherwise, if offline, serve cache or enqueue ---
  async (err) => {
    const status = err.response?.status;
    if (status === 401) {
      const path = window.location.pathname;
      if (path !== "/login") {
        localStorage.removeItem("amal_token");
        window.location.href = "/login";
      }
      return Promise.reject(err);
    }

    // Network failure (no response) → offline path
    const isNetwork = !err.response;
    const cfg = err.config || {};
    const url = cfg.url || "";
    const method = (cfg.method || "get").toLowerCase();
    if (isNetwork && !shouldSkip(url)) {
      // GET: serve from IDB cache
      if (method === "get") {
        const cached = await serveFromCache(method, url);
        if (cached !== undefined) {
          return { data: cached, status: 200, headers: {}, config: cfg, fromCache: true };
        }
      }
      // Writes: enqueue and return synthetic 202 response
      if (["post", "put", "patch", "delete"].includes(method)) {
        await enqueueOperation({
          method,
          url,
          data: cfg.data ? (typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data) : undefined,
          headers: cfg.headers ? { "Content-Type": cfg.headers["Content-Type"] } : undefined,
        });
        return {
          data: { offline: true, queued: true, ...(typeof cfg.data === "string" ? safeParse(cfg.data) : cfg.data || {}) },
          status: 202,
          headers: { "x-offline": "queued" },
          config: cfg,
          offlineQueued: true,
        };
      }
    }
    return Promise.reject(err);
  }
);

function safeParse(s) {
  try { return JSON.parse(s); } catch { return {}; }
}

// Boot offline sync engine after the api instance exists
initOfflineSync(api);
// Try an initial sync if we come back online with leftover queue from previous session
if (navigator.onLine) setTimeout(() => syncQueue().catch(() => {}), 2000);

export function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatFCFA(amount) {
  if (amount === null || amount === undefined) return "0 FCFA";
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(amount) + " FCFA";
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR");
  } catch {
    return iso;
  }
}

export const LOGO_URL = "https://customer-assets.emergentagent.com/job_a7620a69-63ea-4088-b703-b0ffc8f627c2/artifacts/6j219w5w_imagesk.png";

/**
 * Reliable file download helper.
 */
export function downloadBlob(blob, filename, mimeType = "application/pdf") {
  const file = blob instanceof Blob ? blob : new Blob([blob], { type: mimeType });
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
